import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  BBA_KICKOFF_CALENDLY_URL,
  ensureCalendlyAssetsLoaded,
  buildUserCalendlyPrefill,
} from '../../utils/calendlyWidget';

/**
 * Inline Calendly embed for step 1 of Getting Started (kickoff call booking).
 * Pass `url` to schedule a different event type (e.g. the locked dashboard's
 * check-in call) with the same widget behavior.
 *
 * Calendly's widget.js only auto-mounts `.calendly-inline-widget` elements that
 * exist when it loads, so a panel the client toggles open has to mount itself:
 * wait for window.Calendly, then initInlineWidget into our own div. Booking
 * confirmation arrives as a postMessage from calendly.com — that's what tells
 * the gate the step is done.
 */
const KickoffCallScheduler = ({ darkMode, onScheduled, url = BBA_KICKOFF_CALENDLY_URL }) => {
  const { currentUser } = useAuth();
  const hostRef = useRef(null);
  const onScheduledRef = useRef(onScheduled);

  useEffect(() => {
    onScheduledRef.current = onScheduled;
  }, [onScheduled]);

  // Mount the widget once the script is ready. Re-running would double-mount,
  // so the guard is the iframe Calendly injects into our host div.
  useEffect(() => {
    ensureCalendlyAssetsLoaded();
    const host = hostRef.current;
    if (!host) return undefined;

    const prefill = buildUserCalendlyPrefill(currentUser, null);
    let tries = 0;
    const mount = () => {
      if (!hostRef.current || hostRef.current.querySelector('iframe')) return true;
      if (!window.Calendly?.initInlineWidget) return false;
      try {
        window.Calendly.initInlineWidget({
          url,
          parentElement: hostRef.current,
          prefill,
        });
      } catch (err) {
        console.error('Error initializing kickoff Calendly widget:', err);
      }
      return true;
    };

    if (mount()) return () => { host.innerHTML = ''; };
    const id = setInterval(() => {
      tries += 1;
      if (mount() || tries > 100) clearInterval(id);
    }, 100);
    return () => {
      clearInterval(id);
      host.innerHTML = '';
    };
  }, [currentUser, url]);

  // Calendly posts { event, payload } from its own origin when a time is booked.
  useEffect(() => {
    const handleMessage = (e) => {
      if (typeof e.origin === 'string' && !e.origin.includes('calendly.com')) return;
      const data = e?.data;
      if (!data || typeof data !== 'object' || data.event !== 'calendly.event_scheduled') return;
      const payload = data.payload || {};
      onScheduledRef.current?.({
        eventUri: payload.event?.uri || '',
        inviteeUri: payload.invitee?.uri || '',
        source: 'calendly',
      });
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      <div ref={hostRef} style={{ minWidth: 320, height: 700 }} />
    </div>
  );
};

export default KickoffCallScheduler;
